import React, { useState } from 'react';
import {
    getCurrentWindowWidth,
    getCurrentWindowHeight,
} from '@gumtree/shared/src/util/breakpoints-service';

import './maps.scss';
import Maps from './maps';
import {
    MapsContainer,
    ModalDialog,
    OverlapContainer,
    StyledIFrame,
} from './expandable-embedded-map.style';
import { qaAttribute } from '../utils/qa-service';

export interface Props {
    height: string;
    lat: any;
    long: any;
    embeddedMapApiKey: string;
    dynamicMapApiKey: string;
    refreshMap?: boolean;
    width: string;
    zoom?: number;
    disableDragging?: boolean;
}

const ExpandableEmbeddedMap: React.FC<Props> = ({
    refreshMap = true,
    embeddedMapApiKey,
    dynamicMapApiKey,
    zoom = 14,
    lat,
    long,
    width = '100%',
    height = '200px',
    disableDragging = false,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const openHandler = () => {
        setIsModalOpen(true);
    };

    const onKeyDown = (event) => {
        if (event.key === 'Enter') {
            openHandler();
        }
    };

    const closeHandler = () => {
        setIsModalOpen(false);
    };

    const getWidth = () => {
        const hasWindow = typeof window !== 'undefined';

        if (hasWindow && getCurrentWindowWidth(window) <= 767) {
            return '100%';
        }

        return '700px';
    };

    const getHeight = () => {
        const hasWindow = typeof window !== 'undefined';

        if (
            hasWindow &&
            (getCurrentWindowWidth(window) <= 767 || getCurrentWindowHeight(window) <= 700)
        ) {
            // We're on mobile, or a device with a small height.  Limit the height to 75% of the overall window.
            return `${getCurrentWindowHeight() * 0.75}px`;
        }

        return '700px';
    };

    return (
        <>
            <ModalDialog isOpen={isModalOpen} onClose={closeHandler} displayAsSheetOnMweb>
                <MapsContainer>
                    <Maps
                        refreshMap={refreshMap}
                        mapApi={dynamicMapApiKey}
                        zoom={zoom}
                        lat={lat}
                        long={long}
                        width={getWidth()}
                        height={getHeight()}
                        disableDragging={disableDragging}
                    />
                </MapsContainer>
            </ModalDialog>

            <StyledIFrame
                {...qaAttribute('expandable-embedded-map')}
                title="Map"
                width={`${width}`}
                suppressHydrationWarning
                height={`${height}`}
                loading="lazy"
                src={`https://www.google.com/maps/embed/v1/view?key=${embeddedMapApiKey}&center=${parseFloat(
                    lat
                )},${parseFloat(long)}&zoom=14.5`}
            />

            <OverlapContainer
                role="button"
                tabIndex={0}
                height={height}
                onClick={openHandler}
                onKeyDown={onKeyDown}
            >
                <svg>
                    <circle cx="50%" cy="50%" r="80" fill="#007AFF" fillOpacity="0.1" />
                </svg>
            </OverlapContainer>
        </>
    );
};

export default ExpandableEmbeddedMap;
